/**
 * Apply a poll delta to the transcript already on screen.
 *
 * Pure and separate from the hook because this is where the interesting bugs
 * live, and a pure function can be tested exhaustively without a query client.
 *
 * Rules, all of which have a test:
 *  - A message not seen before is INSERTED.
 *  - A message already present is REPLACED IN PLACE — not appended, or an edit
 *    would duplicate the message, and not merged field-by-field, since the
 *    server's row is authoritative for every field including the tombstone.
 *  - A tombstone (`deleted_at` set) stays in the list. It is what the renderer
 *    draws "This message was deleted" from; dropping it here would make a
 *    delete invisible until a full refetch.
 *  - Ordering is by `created_at` and is re-established after every merge, so a
 *    delta arriving out of order (or a slow message committing after a faster
 *    one) still lands in the right place.
 */
export function mergeMessageDelta(current, delta) {
    if (delta.length === 0)
        return current;
    const byId = new Map();
    for (const m of current)
        byId.set(m.id, m);
    for (const m of delta)
        byId.set(m.id, m);
    return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}
/**
 * Group a flat transcript into threads (reply depth is capped at 1).
 *
 * A reply whose root is not in the loaded window is promoted to a root of its
 * own rather than dropped — otherwise scrolling back far enough would make
 * replies vanish, which reads as data loss.
 */
export function groupIntoThreads(messages) {
    const threads = new Map();
    for (const m of messages) {
        if (m.parent_message_id === null) {
            const existing = threads.get(m.id);
            threads.set(m.id, { root: m, replies: existing?.replies ?? [] });
        }
    }
    for (const m of messages) {
        if (m.parent_message_id === null)
            continue;
        const thread = threads.get(m.thread_root_id);
        if (thread) {
            thread.replies.push(m);
        }
        else {
            threads.set(m.id, { root: m, replies: [] });
        }
    }
    const out = [...threads.values()];
    for (const t of out) {
        t.replies.sort((a, b) => a.created_at.localeCompare(b.created_at));
    }
    return out.sort((a, b) => a.root.created_at.localeCompare(b.root.created_at));
}
/**
 * Whether a message continues a run by the same author, so the renderer can
 * drop the repeated avatar and name — the single cheapest thing that makes a
 * transcript read like Teams rather than like a table of rows.
 *
 * A tombstone never continues a run: it gets its own line so a deletion is
 * visible rather than folded into someone's paragraph.
 */
const GROUPING_WINDOW_MS = 5 * 60 * 1000;
export function continuesRun(previous, current) {
    if (!previous)
        return false;
    if (previous.author_id !== current.author_id)
        return false;
    if (previous.deleted_at !== null || current.deleted_at !== null)
        return false;
    const gap = new Date(current.created_at).getTime() - new Date(previous.created_at).getTime();
    return gap >= 0 && gap < GROUPING_WINDOW_MS;
}
/** The day divider label for a message, or null when it shares the previous
 *  message's day. */
export function dayDividerFor(previous, current) {
    const day = new Date(current.created_at).toDateString();
    if (previous && new Date(previous.created_at).toDateString() === day)
        return null;
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (day === today)
        return 'Today';
    if (day === yesterday)
        return 'Yesterday';
    return new Date(current.created_at).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    });
}
//# sourceMappingURL=message-merge.js.map